import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useDecodedToken } from '../../utils/useDecodedToken';

interface FeatureGuardProps {
  children: React.ReactNode;
  feature: 'accounts' | 'transactions' | 'payments' | 'statements' | 'savings';
  fallbackMessage?: string;
  showAlert?: boolean;
}

// Features that are available for each user type
const FEATURE_ACCESS = {
  existing_customer: ['accounts', 'transactions', 'payments', 'statements', 'savings', 'loans'],
  loan_applicant: ['loans', 'payments'], // Loan tracking, management, and loan application payments
  new_user: ['loans', 'payments'] // Loan applications and loan application payments
};

const FEATURE_MESSAGES = {
  accounts: 'Account information will be available once your loan is approved and your account is created in our system.',
  transactions: 'Transaction history will be available once your loan is approved and your account is created in our system.',
  payments: 'Payment features are available for loan applications. Full payment features will be available once your loan is approved and your account is created in our system.',
  statements: 'Account statements will be available once your loan is approved and your account is created in our system.',
  savings: 'Savings account features will be available once your loan is approved and your account is created in our system.',
};

export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  children,
  feature,
  fallbackMessage,
  showAlert = true
}) => {
  const user = useDecodedToken();

  // If no user, don't show anything
  if (!user) {
    return null;
  }

  // Derive user type with stricter full-access requirement
  const hasValidAccount = !!user.accountNumber && String(user.accountNumber).trim() !== '' && String(user.accountNumber).toLowerCase() !== 'undefined' && String(user.accountNumber).toLowerCase() !== 'null';
  const isFullAccess = !!user.hasFullAccess && hasValidAccount;
  const userType = isFullAccess
    ? 'existing_customer'
    : user.trackingNumber
      ? 'loan_applicant'
      : 'new_user';
  
  // Debug logging
  
  
  // Check if user has access to this feature
  const hasAccess = FEATURE_ACCESS[userType]?.includes(feature);



  if (hasAccess) {
    return <>{children}</>;
  }

  // Show fallback for restricted features
  if (!showAlert) {
    return null;
  }

  const message = fallbackMessage || FEATURE_MESSAGES[feature] || 
    'This feature will be available once your loan is approved and your account is created in our system.';

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Feature Not Available
        </Typography>
        <Typography variant="body1">
          {message}
        </Typography>
        {(userType === 'loan_applicant' || userType === 'new_user') && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            You can track and manage your loan applications in the Loans section.
          </Typography>
        )}
      </Alert>
    </Box>
  );
};

export default FeatureGuard;

